import { useEffect, useId, useRef, useState, type FormEvent, type JSX } from 'react';

import { cn } from '../lib/cn.js';
import { BarreDeSaisie } from './BarreDeSaisie.js';
import type { EntreeDeMention } from './mentions.js';
import type { PortDeFil } from './port.js';
import { brouillonApresEchec, creerFileDEnvoi, messageDEchecDEnvoi, type FileDEnvoi } from './fileDEnvoi.js';
import { cleDeConversation } from './brouillons.js';
import { useBrouillon, useMagasinDeBrouillons } from './FournisseurDeBrouillons.js';

export interface ComposeurConversationProps {
  conversationId: string;
  port: Pick<PortDeFil, 'envoyer'>;
  entrees: readonly EntreeDeMention[];
  /** Repondre dans un fil : la racine. */
  parentId?: string | undefined;
  placeholder?: string | undefined;
  /** Le panneau de 24rem du centre : marges serrees, pas de legende. */
  compact?: boolean | undefined;
  autoFocus?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Pourquoi on ne peut pas ecrire (G16) : le champ le dit a la place du placeholder. */
  lectureSeule?: string | undefined;
  /**
   * Appele AVANT l'envoi : rend la conversation ou ecrire (le chatter d'une
   * fiche est cree au premier message par `ouvrirChatter`). Absent :
   * `conversationId` tel quel. Appele UNE fois tant qu'il reussit : trois
   * messages tapes vite avant que le chatter existe n'en creent qu'un. L'hote
   * garde le composeur MONTE pendant cette creation (sa `key` ne suit pas
   * l'id qui nait), sinon la file d'envoi partirait avec lui.
   */
  avantEnvoi?: (() => Promise<string>) | undefined;
  onEnvoye?: ((texte: string) => void) | undefined;
  /**
   * Un texte a poser en tete du brouillon (« Demander a Lia » du centre pose
   * `@Lia `) ; `n` change a chaque demande, meme pour un texte identique.
   */
  insertion?: { texte: string; n: number } | undefined;
  /**
   * La cle du brouillon (`brouillons.ts`). Absente : celle de la conversation
   * (et du fil, si `parentId`). `null` : un brouillon sans conversation
   * identifiable, en memoire, propre a ce composeur. Un hote dont la
   * conversation n'existe pas encore (chatter sans message) donne une cle
   * stable, pour que le brouillon d'une fiche soit garde lui aussi.
   */
  cleDeBrouillon?: string | null | undefined;
}

/**
 * LE COMPOSEUR D'UNE CONVERSATION : une bande filetee en bas (pas une boite),
 * le champ a mentions, Entree envoie, Maj+Entree saute une ligne, l'erreur
 * en ligne avec sa raison (G16). Pilote par props : `port={sdk.messages}`.
 *
 * ENVOI SANS BLOCAGE (retouches du 06/10/2026) : Entree vide le champ et garde
 * le focus tout de suite ; le message part en arriere-plan, dans l'ordre
 * (`fileDEnvoi.ts`). Un echec remet le texte dans le brouillon, avant ce qu'on
 * a tape depuis, et dit pourquoi.
 *
 * LE BROUILLON EST PAR CONVERSATION (`useBrouillon`) : il reste quand on passe
 * a une autre, survit a un rechargement et s'efface a l'envoi. Un echec le
 * rend a la cle ou le message a ete ecrit, meme si l'on est parti ailleurs.
 */
export function ComposeurConversation({
  conversationId, port, entrees, parentId, placeholder, compact, autoFocus, disabled, lectureSeule, avantEnvoi, onEnvoye, insertion, cleDeBrouillon,
}: ComposeurConversationProps): JSX.Element {
  const identifiantMemoire = useId();
  const cle = cleDeBrouillon !== undefined ? cleDeBrouillon : conversationId !== '' ? cleDeConversation(conversationId, parentId) : null;
  const cleEffective = cle ?? `memoire:${identifiantMemoire}`;
  const [brouillon, setBrouillon] = useBrouillon(cleEffective);
  const magasin = useMagasinDeBrouillons();
  const magasinCourant = useRef(magasin);
  magasinCourant.current = magasin;
  useEffect(() => {
    if (!insertion || insertion.texte === '') return;
    setBrouillon((courant) => courant.startsWith(insertion.texte) ? courant : `${insertion.texte}${courant}`);
  }, [insertion?.n]);
  const [erreur, setErreur] = useState<string | null>(null);
  const inerte = disabled === true || lectureSeule !== undefined;
  const formulaire = useRef<HTMLFormElement>(null);
  const onEnvoyeCourant = useRef(onEnvoye);
  onEnvoyeCourant.current = onEnvoye;
  // La conversation creee par `avantEnvoi`, partagee par les envois en file.
  const cibleCreee = useRef<Promise<string> | null>(null);
  // Chaque envoi porte la cle du brouillon d'OU il part : un echec y remet le texte, meme apres un
  // changement de conversation ou un demontage. Si `avantEnvoi` cree la conversation, la cle devient la sienne.
  const file = useRef<FileDEnvoi<{ cle: string }> | null>(null);
  file.current ??= creerFileDEnvoi<{ cle: string }>({
    surEnvoye: ({ texte }) => onEnvoyeCourant.current?.(texte),
    surEchec: (rendus, cause) => {
      const parCle = new Map<string, string[]>();
      for (const { texte, cible } of rendus) parCle.set(cible.cle, [...(parCle.get(cible.cle) ?? []), texte]);
      for (const [cleDeRetour, textes] of parCle) {
        magasinCourant.current.ecrire(cleDeRetour, brouillonApresEchec(textes, magasinCourant.current.lire(cleDeRetour)));
      }
      setErreur(messageDEchecDEnvoi(cause, rendus.length));
    },
  });

  function envoyer(): void {
    // Le brouillon est lu et vide dans le magasin, de facon synchrone : un second Entree avant le
    // rendu suivant trouve un champ vide, ce qui empeche le double envoi.
    const texte = magasin.lire(cleEffective).trim();
    if (texte === '' || inerte) return;
    magasin.ecrire(cleEffective, '');
    setErreur(null);
    formulaire.current?.querySelector('textarea')?.focus({ preventScroll: true });
    const options = parentId !== undefined ? { parentId } : undefined;
    const creer = avantEnvoi;
    const charge = { cle: cleEffective };
    file.current?.ajouter(texte, charge, async () => {
      let cible = conversationId;
      if (creer !== undefined) {
        cibleCreee.current ??= creer();
        try { cible = await cibleCreee.current; } catch (cause) { cibleCreee.current = null; throw cause; }
        charge.cle = cleDeConversation(cible, parentId);
      }
      await port.envoyer(cible, texte, options);
    });
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    envoyer();
  }

  return <form ref={formulaire} onSubmit={onSubmit} data-composeur-conversation
    className={cn('shrink-0 bg-surface', compact ? 'px-2.5 pb-2.5 pt-1' : 'px-5 pb-3 pt-1.5')}>
    {/* P21 : le champ sur un fond doux, sans contour, meme au clic ; aucun filet au-dessus. */}
    <BarreDeSaisie
      value={brouillon}
      onChange={setBrouillon}
      entrees={entrees}
      onEnvoyer={envoyer}
      peutEnvoyer={brouillon.trim() !== '' && !inerte}
      placeholder={lectureSeule ?? placeholder ?? 'Écrire un message… @ cite une personne'}
      ariaLabel="Votre message"
      autoFocus={autoFocus}
      disabled={inerte}
      lignesMax={compact ? 8 : 10}
    />
    {erreur !== null && <p role="alert" className="mt-1.5 text-xs leading-4 text-danger-ink">{erreur}</p>}
    {!compact && erreur === null && <p className="mt-1.5 text-[0.6875rem] leading-4 text-ink-400">
      Entrée envoie · Maj+Entrée saute une ligne · @ cite une personne · @Lia répond une fois, avec vos droits
    </p>}
  </form>;
}
